import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import LandingNav from '../components/shared/LandingNav';
import SiteFooter from '../components/shared/SiteFooter';
import Seo from '../components/Seo';
import BlogArticleBody from '../components/blog/BlogArticleBody';
import BlogGenerateControls from '../components/blog/BlogGenerateControls';
import { useTheme } from '../contexts/ThemeContext';
import { AuthContext } from '../contexts/AuthContext';
import {
  approveBlogPost,
  deleteBlogPost,
  fetchBlogPostById,
  regenerateBlogPost,
  rejectBlogPost,
  updateBlogPost,
  uploadBlogCover,
} from '../services/blogApi';
import '../components/blog/Blog.css';

const BlogEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { user } = useContext(AuthContext) || {};
  const token = user?.token;
  const generateRef = useRef(null);

  const [mode, setMode] = useState('read'); // 'read' | 'edit'
  const [post, setPost] = useState(null);
  const [payload, setPayload] = useState(null);
  const [title, setTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyAction, setBusyAction] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchBlogPostById(id, token);
      setPost(data.post);
      setPayload(data.post.payload);
      setTitle(data.post.title || '');
      setMetaDescription(data.post.meta_description || '');
      setSlug(data.post.slug || '');
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  const updateSection = (index, field, value) => {
    setPayload((prev) => {
      const next = structuredClone(prev);
      next.content.sections[index][field] = value;
      return next;
    });
  };

  const deleteSection = (index) => {
    setPayload((prev) => {
      const next = structuredClone(prev);
      next.content.sections = (next.content.sections || []).filter((_, i) => i !== index);
      return next;
    });
  };

  const addSection = () => {
    setPayload((prev) => {
      const next = structuredClone(prev);
      if (!next.content) next.content = { h1: '', sections: [], faq: [], cta: {} };
      if (!next.content.sections) next.content.sections = [];
      next.content.sections.push({ heading: '', level: 2, body: '', subsections: [] });
      return next;
    });
  };

  const updateCta = (field, value) => {
    setPayload((prev) => {
      const next = structuredClone(prev);
      if (!next.content) next.content = {};
      next.content.cta = {
        placement: next.content.cta?.placement || 'end',
        text: next.content.cta?.text || '',
        target_url: next.content.cta?.target_url || '/pricing',
        ...next.content.cta,
        [field]: value,
      };
      return next;
    });
  };

  const updateH1 = (value) => {
    setPayload((prev) => {
      const next = structuredClone(prev);
      next.content.h1 = value;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const nextPayload = structuredClone(payload);
      if (nextPayload.seo) {
        nextPayload.seo.title_tag = title;
        nextPayload.seo.meta_description = metaDescription;
        nextPayload.seo.slug = slug;
      }
      const data = await updateBlogPost(id, token, {
        payload: nextPayload,
        title,
        meta_description: metaDescription,
        slug,
      });
      setPost(data.post);
      setPayload(data.post.payload);
      setMessage('Saved');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (name, fn) => {
    setBusyAction(name);
    setError(null);
    setMessage(null);
    try {
      const data = await fn();
      setPost(data.post);
      setPayload(data.post.payload);
      setTitle(data.post.title || '');
      setMetaDescription(data.post.meta_description || '');
      setSlug(data.post.slug || '');
      setMessage(name === 'approve' ? 'Published' : name === 'reject' ? 'Hidden' : 'Done');
      if (name === 'approve' && data.post?.slug) {
        navigate(`/blog/${data.post.slug}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyAction(null);
    }
  };

  const handleRegenerate = async () => {
    const gen = generateRef.current?.getPayload?.() || { valid: true };
    if (!gen.valid) {
      setError(gen.error || 'Invalid direction');
      return;
    }
    setBusyAction('regen');
    setError(null);
    setMessage(null);
    try {
      const data = await regenerateBlogPost(id, token, {
        topic: gen.topic,
        direction: gen.direction,
        sendEmail: true,
      });
      if (data.jobId) {
        navigate(`/blog/generating/${data.jobId}`);
        return;
      }
      setMessage('Queued');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyAction(null);
    }
  };

  const handleCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusyAction('cover');
    setError(null);
    setMessage(null);
    try {
      const data = await uploadBlogCover(id, token, file);
      setPost(data.post);
      setMessage('Cover updated');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyAction(null);
      e.target.value = '';
    }
  };

  const handleDelete = async () => {
    if (!post || busyAction) return;
    if (post.status === 'published') {
      setError('Published posts cannot be deleted');
      return;
    }
    const confirmed = window.confirm('Delete this post permanently? This cannot be undone.');
    if (!confirmed) return;

    setBusyAction('delete');
    setError(null);
    setMessage(null);
    try {
      await deleteBlogPost(id, token);
      navigate('/blog');
    } catch (err) {
      setError(err.message || 'Delete failed');
    } finally {
      setBusyAction(null);
    }
  };

  const text = colors.text;
  const muted = colors.textMuted || colors.textLight || '#94a3b8';
  const border = colors.border || 'rgba(148,163,184,0.25)';

  return (
    <div className="blog-page" style={{ backgroundColor: colors.bgPrimary, color: text }}>
      <Seo title="Edit blog post | PrecisionTrader" noindex canonicalPath={`/blog/edit/${id}`} />
      <LandingNav />

      {loading && (
        <div className="blog-reading-col">
          <p style={{ color: muted }}>Loading…</p>
        </div>
      )}
      {error && !post && (
        <div className="blog-reading-col">
          <p style={{ color: colors.danger || '#f87171' }}>{error}</p>
          <Link to="/blog" style={{ color: muted }}>
            ← Blog
          </Link>
        </div>
      )}

      {post && payload && (
        <>
          <div className="blog-edit-top">
            <div className="blog-edit-top-nav">
              <Link to="/blog" style={{ color: muted, textDecoration: 'none', fontSize: '0.9rem' }}>
                ← Blog
              </Link>
              {post.status === 'published' && (
                <>
                  <span style={{ color: muted }}>·</span>
                  <Link
                    to={`/blog/${post.slug}`}
                    style={{ color: muted, textDecoration: 'none', fontSize: '0.9rem' }}
                  >
                    View live
                  </Link>
                </>
              )}
              <span
                className={`blog-status-pill blog-status-pill--${
                  post.status === 'published'
                    ? 'published'
                    : post.status === 'rejected'
                      ? 'hidden'
                      : 'in-review'
                }`}
                style={{ marginLeft: 'auto' }}
              >
                {post.status === 'published'
                  ? 'Published'
                  : post.status === 'rejected'
                    ? 'Hidden'
                    : 'In Review'}
              </span>
            </div>

            <div className="blog-mode-toolbar">
              <div className="blog-mode-tabs" role="tablist" aria-label="Editor mode">
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === 'read'}
                  className={`blog-mode-tab${mode === 'read' ? ' is-active' : ''}`}
                  onClick={() => setMode('read')}
                >
                  Read
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === 'edit'}
                  className={`blog-mode-tab${mode === 'edit' ? ' is-active' : ''}`}
                  onClick={() => setMode('edit')}
                >
                  Edit
                </button>
              </div>
              <button
                type="button"
                className="blog-mode-approve"
                onClick={() => runAction('approve', () => approveBlogPost(id, token))}
                disabled={!!busyAction || post.status === 'published'}
              >
                {busyAction === 'approve' ? '…' : 'Approve'}
              </button>
            </div>
          </div>

          <div className="blog-edit-layout">
            <div className="blog-edit-main">
              {mode === 'read' ? (
                <article>
                  {post.cover_image_url && (
                    <img
                      src={post.cover_image_url}
                      alt=""
                      style={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                        borderRadius: '4px',
                        marginBottom: '1.5rem',
                      }}
                    />
                  )}
                  <h1
                    style={{
                      fontSize: 'clamp(1.75rem, 3vw, 2.4rem)',
                      fontWeight: 650,
                      lineHeight: 1.2,
                      margin: '0 0 0.75rem',
                    }}
                  >
                    {payload.content?.h1 || title}
                  </h1>
                  {(metaDescription || payload.seo?.meta_description) && (
                    <p style={{ color: muted, fontSize: '1.1rem', lineHeight: 1.5, marginBottom: '2rem' }}>
                      {metaDescription || payload.seo?.meta_description}
                    </p>
                  )}
                  <BlogArticleBody payload={payload} colors={colors} />
                </article>
              ) : (
                <div className="blog-edit-fields">
                  <p style={{ margin: '0 0 1.25rem', color: muted, fontSize: '0.9rem' }}>
                    Edit content below. Switch to Read anytime to preview.
                  </p>

                  <label>Title tag</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} />

                  <label>Slug</label>
                  <input value={slug} onChange={(e) => setSlug(e.target.value)} />

                  <label>Meta description</label>
                  <textarea
                    rows={3}
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                  />

                  <label>H1</label>
                  <input
                    value={payload.content?.h1 || ''}
                    onChange={(e) => updateH1(e.target.value)}
                  />

                  {(payload.content?.sections || []).map((section, i) => (
                    <div key={i} className="blog-edit-section-block">
                      <div className="blog-edit-section-head">
                        <label style={{ marginBottom: 0 }}>Section {i + 1}</label>
                        <button
                          type="button"
                          className="blog-edit-delete-section"
                          onClick={() => deleteSection(i)}
                        >
                          Delete section
                        </button>
                      </div>
                      <label>Heading</label>
                      <input
                        value={section.heading || ''}
                        onChange={(e) => updateSection(i, 'heading', e.target.value)}
                      />
                      <label>Body</label>
                      <textarea
                        rows={8}
                        value={section.body || ''}
                        onChange={(e) => updateSection(i, 'body', e.target.value)}
                      />
                    </div>
                  ))}

                  <button type="button" className="blog-edit-add-section" onClick={addSection}>
                    Add section
                  </button>

                  <div className="blog-edit-section-block">
                    <label>Call to action — text</label>
                    <textarea
                      rows={3}
                      value={payload.content?.cta?.text || ''}
                      onChange={(e) => updateCta('text', e.target.value)}
                    />
                    <label>Call to action — URL</label>
                    <input
                      value={payload.content?.cta?.target_url || ''}
                      onChange={(e) => updateCta('target_url', e.target.value)}
                      placeholder="/pricing"
                    />
                    <label>Call to action — placement</label>
                    <input
                      value={payload.content?.cta?.placement || 'end'}
                      onChange={(e) => updateCta('placement', e.target.value)}
                      placeholder="end"
                    />
                  </div>
                </div>
              )}
            </div>

            <aside
              className="blog-edit-panel"
              style={{
                padding: '1.25rem',
                border: `1px solid ${border}`,
                borderRadius: '8px',
                background: colors.bgCard || 'transparent',
              }}
            >
              <p style={{ margin: '0 0 0.85rem', fontWeight: 600 }}>Publish</p>

              {(error || message) && (
                <p
                  style={{
                    marginBottom: '0.75rem',
                    fontSize: '0.85rem',
                    color: error ? colors.danger || '#f87171' : muted,
                  }}
                >
                  {error || message}
                </p>
              )}

              <div className="blog-edit-actions">
                <button
                  type="button"
                  className="primary"
                  onClick={handleSave}
                  disabled={saving || !!busyAction}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  className="danger"
                  onClick={() => runAction('reject', () => rejectBlogPost(id, token))}
                  disabled={!!busyAction}
                >
                  {busyAction === 'reject' ? '…' : 'Hide'}
                </button>
                <button type="button" onClick={handleRegenerate} disabled={!!busyAction}>
                  {busyAction === 'regen' ? 'Starting…' : 'Generate another'}
                </button>
                <button
                  type="button"
                  className="danger"
                  onClick={handleDelete}
                  disabled={!!busyAction || post.status === 'published'}
                >
                  {busyAction === 'delete' ? 'Deleting…' : 'Delete'}
                </button>
              </div>

              <div className="blog-review-cover">
                <label style={{ display: 'block', marginBottom: '0.45rem', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: muted }}>
                  Cover image
                </label>
                {post.cover_image_url ? (
                  <img
                    src={post.cover_image_url}
                    alt=""
                    className="blog-review-cover-preview"
                  />
                ) : (
                  <div className="blog-review-cover-empty" aria-hidden />
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCover}
                  disabled={!!busyAction}
                />
                <p style={{ margin: '0.4rem 0 0', fontSize: '0.75rem', color: muted, lineHeight: 1.4 }}>
                  1200×630 recommended · max 2MB
                </p>
              </div>

              <div className="blog-edit-regen">
                <BlogGenerateControls ref={generateRef} token={token} compact />
              </div>
            </aside>
          </div>
        </>
      )}

      <SiteFooter />
    </div>
  );
};

export default BlogEdit;
